import type { AuthUser } from "./session.types"

export interface ParsedAuthUserName {
  firstName: string
  lastName: string
}

/**
 * Parses a Cognito AuthUser's name attributes into a structured firstName/lastName pair.
 *
 * Resolution order:
 * 1. `givenName` / `familyName` (set when the user provided them explicitly, e.g. signup or social login)
 * 2. `name` (split on whitespace — first token = firstName, remainder = lastName)
 *
 * Returns `null` when neither a firstName nor a lastName can be derived, so callers
 * can decide how to handle the missing-name case (e.g. show an error, prompt the user).
 */
export const parseAuthUserName = (
  user: AuthUser
): ParsedAuthUserName | null => {
  const nameParts = (user.name || "").trim().split(/\s+/).filter(Boolean)
  const firstName = user.givenName || nameParts[0] || ""
  const lastName = user.familyName || nameParts.slice(1).join(" ") || ""

  if (!firstName || !lastName) {
    return null
  }

  return { firstName, lastName }
}
